/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getEvaluacionesAsistencia,
  postEvaluacionAsistencia,
  deleteEvaluacionAsistencia,
  patchEvaluacionAsistencia,
  getEvaluacionAsistencia,
  autorizarEvaluacionAsistencia,
  cancelarEvaluacionAsistencia,
  reactivarEvaluacionAsistencia,
} from 'api/catalogos/Clock/TipoEvaluacionAsistencia';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';

export const tipoDeEvaluacionAsistenciaPermission = {
  permissionModel: 'tipodeevaluaciondeasistencia',
  catalogsModels: [],
  tabsModels: [],
};

const ClockEvaluacionAsistencia = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleReactivar, setVisibleReactivar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getEvaluacionesAsistencia();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getEvaluacionesAsistencia(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setVisibleCancelar(false);
    setVisibleReactivar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _nivelDeEstudio = await getEvaluacionAsistencia(match.id);
    setTimeout(() => {
      if (_nivelDeEstudio) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_nivelDeEstudio));
          setDisableFields(_nivelDeEstudio?.estados_globales !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchEvaluacionAsistencia(key, values) : postEvaluacionAsistencia(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteEvaluacionAsistencia(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un Tipo de Evaluación de Asistencia ya autorizado');
        return;
      }
      const saved = await autorizarEvaluacionAsistencia(key);
      if (saved) {
        const _data = await getEvaluacionesAsistencia();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmCancelar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar un Tipo de Evaluación de Asistencia ya cancelado');
        return;
      }
      const saved = await cancelarEvaluacionAsistencia(key);
      if (saved) {
        const _data = await getEvaluacionesAsistencia();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmReactivar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede reactivar un Tipo de Evaluación de Asistencia ya activo');
        return;
      }
      const saved = await reactivarEvaluacionAsistencia(key);
      if (saved) {
        const _data = await getEvaluacionesAsistencia();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Descripción',
      dataIndex: 'nombre',
      key: 'descripcion',
      width: 400,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'tipodeevaluaciondeasistencia', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
            {
              onClick: () => setVisibleCancelar(true),
              permission: { model: 'tipodeevaluaciondeasistencia', action: 'cancelar' },
              icon: <CloseCircleOutlined />,
              text: 'Cancelar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales === ESTATUS_CANCELADO,
              className: 'cancel',
            },
            {
              onClick: () => setVisibleReactivar(true),
              permission: { model: 'tipodeevaluaciondeasistencia', action: 'reactivar' },
              icon: <RedoOutlined />,
              text: 'Reactivar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_CANCELADO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportParam={false}
          allowSearch={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipos de Evaluación de Asistencia`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
            )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="nombre"
                rules={rules.required}
                label="Descripción"
                hasFeedback
              >
                <Input
                  allowClear
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                  disabled={selectedRowKeys.length <= 0 || disableFields}
                  onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Tipo de Evaluación de Asistencia ${form.getFieldValue('nombre')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar el Tipo de Evaluación de Asistencia ${selectedData?.nombre}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmCancelar}
          ConfirmCancel
          onCancel={() => setVisibleCancelar(false)}
          contentTitle={`Cancelar el Tipo de Evaluación de Asistencia ${selectedData?.nombre}`}
          open={visibleCancelar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleReactivar(false)}
          contentTitle={`Reactivar el Tipo de Evaluación de Asistencia ${selectedData?.nombre}`}
          open={visibleReactivar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar el Tipo de Evaluación de Asistencia ${selectedData?.nombre}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ClockEvaluacionAsistencia;
